import {
  TeacherNotificationResponse,
  TeacherStudentImportItem,
  TeacherStudentImportResult,
  TeacherStudentRecord,
  TeacherStatistics
} from '../../types/teacher'
import { normalizeVatliUrl } from '../../utils/urlNormalizer'
import { API_CONFIG } from '../../config/api.config'

type RequestOptions = {
  method?: 'GET' | 'POST' | 'DELETE'
  body?: any
  searchParams?: Record<string, string | number | undefined>
}

class TeacherStudentService {
  private baseUrl: string
  private usePythonBackend: boolean

  constructor() {
    this.usePythonBackend = API_CONFIG.features.usePythonBackend
    this.baseUrl = this.usePythonBackend
      ? API_CONFIG.python.apiUrl
      : API_CONFIG.wordpress.apiUrl
  }

  private getToken() {
    // Đồng nhất với phần còn lại của app (jwt_token là key chính) — tránh lỗi
    // "cần đăng nhập" ở tab Thống kê khi session chỉ có jwt_token.
    return (
      localStorage.getItem('jwt_token') ||
      localStorage.getItem('auth_token') ||
      localStorage.getItem('token') ||
      ''
    )
  }

  private getPath(path: string): string {
    if (!this.usePythonBackend) return path;

    // Map paths for Python Backend
    // Note: Some legacy endpoints might not have direct equivalents yet
    if (path === '/teacher-students/claim') return '/students/claim';
    if (path === '/teacher-students') return '/students/teacher/students';
    if (path === '/teacher-students/import') return '/students/teacher/students/invite';
    if (path === '/teacher-notifications') return '/students/teacher/notifications';
    if (path === '/teacher-notifications/mark-read') return '/students/teacher/notifications/mark-read';
    if (path === '/teacher-students/statistics') return '/students/teacher/statistics';

    // Fallback for others or if structure matches
    return path;
  }

  private buildUrl(path: string, searchParams?: RequestOptions['searchParams']) {
    const finalPath = this.getPath(path);
    // Ensure accurate base URL handling
    const base = this.baseUrl.endsWith('/') ? this.baseUrl.slice(0, -1) : this.baseUrl;
    const url = new URL(`${base}${finalPath}`, window.location.origin) // Relative to origin if base is relative

    // Handle absolute URLs if baseUrl provides them
    if (this.baseUrl.startsWith('http')) {
      const absoluteUrl = new URL(`${base}${finalPath}`)
      if (searchParams) {
        Object.entries(searchParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            absoluteUrl.searchParams.set(key, String(value))
          }
        })
      }
      return absoluteUrl.toString()
    }

    if (searchParams) {
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.set(key, String(value))
        }
      })
    }
    return url.toString()
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const token = this.getToken()
    // Skip auth check for specific public endpoints if any (none here)
    if (!token && path !== '/public') {
      // Silent fail or throw? For claimRecords called in background, silent fail preferred?
      // But original code threw Error.
      console.warn('TeacherStudentService: No token found, request aborted.');
      throw new Error('Bạn cần đăng nhập để sử dụng chức năng này.')
    }

    try {
      const response = await fetch(this.buildUrl(path, options.searchParams), {
        method: options.method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: options.body ? JSON.stringify(options.body) : undefined
      })

      if (!response.ok) {
        // Handle 404 gracefully for claim?
        if (response.status === 404 && path === '/teacher-students/claim') {
          return {} as T; // No invitations found
        }
        const message = await response.text()
        throw new Error(message || 'Không thể kết nối máy chủ giáo viên.')
      }

      return response.json()
    } catch (error) {
      console.error(`TeacherStudentService Error (${path}):`, error);
      throw error;
    }
  }

  getStudents(params?: { status?: string; search?: string }) {
    return this.request<{ items: TeacherStudentRecord[] }>('/teacher-students', {
      searchParams: params
    })
  }

  importStudents(students: TeacherStudentImportItem[]) {
    return this.request<TeacherStudentImportResult>('/teacher-students/import', {
      method: 'POST',
      body: { students }
    })
  }

  linkStudent(studentEmail: string) {
    return this.request('/teacher-students/link', {
      method: 'POST',
      body: { studentEmail }
    })
  }

  unlinkStudent(studentEmail: string) {
    return this.request('/teacher-students/unlink', {
      method: 'POST',
      body: { studentEmail }
    })
  }

  claimRecords() {
    return this.request('/teacher-students/claim', {
      method: 'POST'
    })
  }

  getNotifications(params?: { status?: 'read' | 'unread'; page?: number }) {
    return this.request<TeacherNotificationResponse>('/teacher-notifications', {
      searchParams: {
        status: params?.status,
        page: params?.page
      }
    })
  }

  markNotificationsRead(notificationIds: number[]) {
    return this.request('/teacher-notifications/mark-read', {
      method: 'POST',
      body: { notificationIds }
    })
  }

  // ✅ NEW: Get teacher statistics with filters
  getStatistics(filters?: {
    class?: string
    lesson?: string
    contentType?: 'all' | 'theory' | 'quiz'
  }) {
    return this.request<TeacherStatistics>('/teacher-students/statistics', {
      searchParams: filters
    })
  }
}

export const teacherStudentService = new TeacherStudentService()

